import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  LayoutDashboard, Layers, Workflow, Plug2, Shield,
  FileText, Bot, Settings, ChevronLeft, ChevronRight,
  Zap, Activity, Users, Server, HardDrive, Network, Wrench,
} from 'lucide-react';
import { OrbaryxLogo } from '../OrbaryxLogo';

interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
  badgeColor?: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Platform',
    items: [
      { label: 'Dashboard', href: '/', icon: LayoutDashboard },
      { label: 'Activity', href: '/activity', icon: Activity },
    ],
  },
  {
    label: 'Automation',
    items: [
      { label: 'Blueprint Catalog', href: '/blueprints', icon: Layers, badge: 'NEW', badgeColor: 'bg-orbayx-500' },
      { label: 'My Workflows', href: '/workflows', icon: Workflow },
      { label: 'Workflow Builder', href: '/builder', icon: Zap },
    ],
  },
  {
    label: 'Domains',
    items: [
      { label: 'Active Directory', href: '/domains/ad', icon: Users },
      { label: 'Security & Cyber', href: '/domains/security', icon: Shield },
      { label: 'Virtualization', href: '/domains/vm', icon: Server },
      { label: 'Storage', href: '/domains/storage', icon: HardDrive },
      { label: 'Network', href: '/domains/network', icon: Network },
      { label: 'Patch Management', href: '/domains/patch', icon: Wrench },
    ],
  },
  {
    label: 'Platform',
    items: [
      { label: 'Integrations', href: '/integrations', icon: Plug2 },
      { label: 'AI Assistant', href: '/ai', icon: Bot, badge: 'BETA', badgeColor: 'bg-emerald-500' },
      { label: 'Audit Log', href: '/audit', icon: FileText },
      { label: 'Settings', href: '/settings', icon: Settings },
    ],
  },
];

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();

  return (
    <div
      className={clsx(
        'flex flex-col h-full bg-white border-r border-gray-200 transition-all duration-300 shadow-sm',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      {/* Logo */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <OrbaryxLogo size={32} />
            <div>
              <span className="font-bold text-gray-800 text-sm">Orbaryx</span>
              <div className="text-[10px] text-gray-400 leading-none">IT Runs Itself</div>
            </div>
          </div>
        )}
        {collapsed && (
          <OrbaryxLogo size={32} className="mx-auto" />
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={clsx('p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors', collapsed && 'mx-auto')}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-6">
        {NAV_GROUPS.map((group) => (
          <div key={group.label}>
            {!collapsed && (
              <div className="px-2 mb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                {group.label}
              </div>
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href || (item.href !== '/' && location.pathname.startsWith(item.href));

                return (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    className={clsx(
                      'flex items-center gap-3 px-2 py-2 rounded-lg text-sm font-medium transition-all duration-150',
                      isActive
                        ? 'bg-orbayx-50 text-orbayx-700 border border-orbayx-200'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100',
                      collapsed && 'justify-center',
                    )}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon className={clsx('flex-shrink-0 w-4 h-4', isActive ? 'text-orbayx-600' : 'text-gray-400')} />
                    {!collapsed && (
                      <>
                        <span className="flex-1 truncate">{item.label}</span>
                        {item.badge && (
                          <span className={clsx('px-1.5 py-0.5 text-[9px] font-bold rounded text-white', item.badgeColor)}>
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      {!collapsed && (
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span>All systems operational</span>
          </div>
          <div className="mt-1 text-[10px] text-gray-300">v1.0.0</div>
        </div>
      )}
    </div>
  );
}
